// screen: AI review (change add-viewstream-v3 §0.5, spec editorial-review) — the review queue of AI artefacts. Every
// AI artefact is a draft until an editor acts: edit the headline / standfirst, approve (= publish: the tenant's site
// shows it and the `artifact.published` webhook fires) or reject with a reason. Hebrew-first.
// v3 §1.5: select drafts and approve or reject them in bulk (numbers cards are never bulk-approved: figure by figure),
// rejection reasons (fixed list + note), edits are versions with the AI's original kept (edit history).
import * as React from 'react';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { Check, Sparkles } from 'lucide-react';
import type { ArticleBody, Scope } from '@viewstream/api-client';
import { Badge, Button, EmptyState, ErrorState, Field, Input, Skeleton, useToast } from '@viewstream/ui';
import { fmtDateTime } from '@viewstream/i18n';
import { appTitle } from '~/app/brand';
import { useTenant } from '~/app/tenant';
import { useLocale } from '~/app/locale';
import { can } from '~/app/permissions';
import { problemText } from '~/app/team';
import { AILabel } from '~/app/AIArticle';
import { NumbersCardDetail } from '~/app/NumbersCard';
import { EditHistory, RejectForm, RejectedReason, useKindLabel } from '~/app/AIReview';
import { ContentSummaryPanel, SummaryReviewBadge } from '~/app/ContentSummary';
import { useSummaryReviews, type SummaryKind, type SummaryReview } from '~/app/summaries';
import {
  clock, useAIArtifact, useAIArtifacts, useApproveArtifact, useBulkApprove, useBulkReject, usePatchArtifact, useRejectArtifact,
  type AIArtifact, type AIArtifactStatus, type AIBulkResult,
} from '~/app/ai';

interface AISearch { id?: string; status?: AIArtifactStatus; type?: 'summary'; kind?: SummaryKind }

export const Route = createFileRoute('/_layout/t/$slug/ai')({
  component: Screen,
  validateSearch: (s: Record<string, unknown>): AISearch => ({
    ...(typeof s['id'] === 'string' && /^[0-9a-f-]{36}$/.test(s['id']) ? { id: s['id'] } : {}),
    ...(['draft', 'published', 'rejected', 'approved'].includes(s['status'] as string) ? { status: s['status'] as AIArtifactStatus } : {}),
    ...(s['type'] === 'summary' ? { type: 'summary' as const } : {}),
    ...(s['kind'] === 'programme' || s['kind'] === 'asset' ? { kind: s['kind'] as SummaryKind } : {}),
  }),
});

const AI_WRITE = 'ai:write' as string as Scope;
const AI_PUBLISH = 'ai:publish' as string as Scope;

function Screen() {
  const { t } = useLingui();
  const { tenant } = useTenant();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const status = search.status ?? 'draft';
  React.useEffect(() => { document.title = appTitle(t`AI review`); }, [t]);
  const tabs: { s: AIArtifactStatus; label: string }[] = [
    { s: 'draft', label: t`Waiting for review` }, { s: 'published', label: t`Published` }, { s: 'rejected', label: t`Rejected` },
  ];
  const go = (next: AISearch) => void navigate({ to: '/t/$slug/ai' as never, params: { slug: tenant.slug } as never, search: next as never });
  return (
    <div className="space-y-6">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-semibold"><Sparkles className="size-6" aria-hidden />{t`AI review`}</h1>
        <p className="mt-1 max-w-3xl text-sm text-fg-muted">{t`Drafts the AI made from your programmes: articles with chapters and quotes. Nothing reaches your site, the player or your webhooks until an editor approves it. Check the facts, names and numbers against the video before you approve.`}</p>
        <Link to={'/t/$slug/entities' as never} params={{ slug: tenant.slug } as never} className="mt-2 inline-block text-sm text-primary underline-offset-4 hover:underline">{t`Companies mentioned in programmes`}</Link>
        {' · '}
        <Link to={'/t/$slug/archive' as never} params={{ slug: tenant.slug } as never} className="mt-2 inline-block text-sm text-primary underline-offset-4 hover:underline">{t`Archive search`}</Link>
      </div>
      {/* 0111 (now14poc demo P0-1): summaries are reviewed here too — filter by type */}
      <div role="tablist" aria-label={t`Type`} className="flex flex-wrap gap-2" data-testid="ai-type">
        <Button role="tab" aria-selected={search.type !== 'summary'} size="sm" variant={search.type !== 'summary' ? 'primary' : 'secondary'} onClick={() => go({ status })}>{t`Articles and clips`}</Button>
        <Button role="tab" aria-selected={search.type === 'summary'} size="sm" variant={search.type === 'summary' ? 'primary' : 'secondary'} onClick={() => go({ status, type: 'summary' })}>{t`Summaries`}</Button>
      </div>
      <div role="tablist" aria-label={t`Status`} className="flex flex-wrap gap-2">
        {tabs.map((x) => (
          <Button key={x.s} role="tab" aria-selected={status === x.s} size="sm" variant={status === x.s ? 'primary' : 'secondary'} onClick={() => go({ status: x.s, type: search.type })}>{x.label}</Button>
        ))}
      </div>
      {search.type === 'summary' ? (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)]">
          <SummaryList review={status === 'published' || status === 'approved' ? 'approved' : status === 'rejected' ? 'rejected' : 'draft'} selected={search.id}
            onSelect={(id, kind) => go({ status, type: 'summary', id, kind })} />
          {search.id && search.kind ? <div className="rounded-lg border border-border p-4"><ContentSummaryPanel key={search.id} kind={search.kind} id={search.id} /></div>
            : <p className="text-sm text-fg-muted">{t`Pick an item to review it.`}</p>}
        </div>
      ) : (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)]">
          <ArtifactList status={status} selected={search.id} onSelect={(id) => go({ status, id })} />
          {search.id ? <ArtifactDetail key={search.id} id={search.id} /> : <p className="text-sm text-fg-muted">{t`Pick an item to review it.`}</p>}
        </div>
      )}
    </div>
  );
}

function ArtifactList({ status, selected, onSelect }: { status: AIArtifactStatus; selected?: string; onSelect: (id: string) => void }) {
  const { t } = useLingui();
  const { tenant, scopes } = useTenant();
  const { locale, tz } = useLocale();
  const kindLabel = useKindLabel();
  const when = (iso: string) => fmtDateTime(iso, { locale, tz }, { dateStyle: 'short', timeStyle: 'short' });
  const q = useAIArtifacts(tenant.slug, status);
  const [picked, setPicked] = React.useState<Set<string>>(new Set());
  React.useEffect(() => { setPicked(new Set()); }, [status]);
  if (q.isPending) return <Skeleton className="h-48" />;
  if (q.isError) return <ErrorState error={q.error} onRetry={() => q.refetch()} labels={{ title: t`Could not load the review queue`, retry: t`Retry`, requestId: t`Request id`, copy: t`Copy` }} />;
  if (q.data.items.length === 0) return <EmptyState icon={<Sparkles className="size-8" aria-hidden />} title={t`Nothing here`} description={status === 'draft' ? t`Ask for an article on an ended programme (Catch-up or EPG → the programme → AI article).` : undefined} />;
  const bulk = status === 'draft' && (can(scopes, AI_WRITE) || can(scopes, AI_PUBLISH));
  const items = q.data.items;
  const visible = items.filter((a) => picked.has(a.id)); // what is still in the list
  const toggle = (id: string) => setPicked((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const allOn = visible.length === items.length;
  return (
    <div className="space-y-3">
      {bulk ? (
        <BulkBar items={visible} onAll={() => setPicked(allOn ? new Set() : new Set(items.map((a) => a.id)))} allOn={allOn} onDone={() => setPicked(new Set())} />
      ) : null}
      <ul className="divide-y divide-border rounded-lg border border-border" aria-label={t`AI items`}>
        {items.map((a) => (
          <li key={a.id} className="flex items-start gap-2">
            {bulk ? (
              <input type="checkbox" className="ms-3 mt-4" aria-label={t`Select ${a.title || a.subject_title}`} checked={picked.has(a.id)} onChange={() => toggle(a.id)} />
            ) : null}
            <button type="button" onClick={() => onSelect(a.id)} aria-current={selected === a.id ? 'true' : undefined}
              className={`block w-full p-3 text-start hover:bg-surface-2 ${selected === a.id ? 'bg-surface-2' : ''}`}>
              <span className="block font-medium" dir="auto">{a.title || a.subject_title}</span>
              <span className="mt-0.5 block text-xs text-fg-muted">
                {kindLabel(a.kind)} ·{a.subject_title}{a.subject_start_at ? ` · ${when(a.subject_start_at)}` : ''}{(a.version ?? 1) > 1 ? ` · ${t`edited`}` : ''}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Bulk approve / reject of the selected drafts. Numbers cards are not approved in bulk (figure by figure). */
function BulkBar({ items, allOn, onAll, onDone }: { items: AIArtifact[]; allOn: boolean; onAll: () => void; onDone: () => void }) {
  const { t } = useLingui();
  const { tenant, scopes } = useTenant();
  const toast = useToast();
  const approve = useBulkApprove(tenant.slug);
  const reject = useBulkReject(tenant.slug);
  const [rejecting, setRejecting] = React.useState(false);
  const approvable = items.filter((a) => a.kind !== 'numbers');
  const numbers = items.length - approvable.length;
  const report = (r: AIBulkResult, verb: 'approved' | 'rejected') => {
    const ok = (r.counts['published'] ?? 0) + (r.counts['rejected'] ?? 0);
    const other = r.items.length - ok;
    toast.push({
      title: verb === 'approved' ? t`${ok} published` : t`${ok} rejected`,
      description: other > 0 ? t`${other} not changed (already handled, numbers card or error)` : undefined,
      tone: other > 0 ? 'neutral' : 'ok',
    });
    onDone();
  };
  const fail = (e: unknown) => toast.push({ title: t`The action failed`, description: problemText(e), tone: 'danger' });
  return (
    <div className="space-y-2 rounded-lg border border-border p-3" aria-label={t`Bulk review`}>
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="ghost" onClick={onAll}>{allOn ? t`Clear selection` : t`Select all`}</Button>
        <span className="text-sm text-fg-muted">{t`${items.length} selected`}</span>
        {can(scopes, AI_PUBLISH) ? (
          <Button size="sm" disabled={approvable.length === 0 || approve.isPending}
            onClick={() => approve.mutate(approvable.map((a) => a.id), { onSuccess: (r) => report(r, 'approved'), onError: fail })}>
            <Check className="size-4" aria-hidden />{t`Approve and publish ${approvable.length}`}
          </Button>
        ) : null}
        {can(scopes, AI_WRITE) ? (
          <Button size="sm" variant="secondary" disabled={items.length === 0} aria-expanded={rejecting} onClick={() => setRejecting(!rejecting)}>{t`Reject selected`}</Button>
        ) : null}
      </div>
      {numbers > 0 ? <p className="text-xs text-fg-muted">{t`Numbers cards are not approved in bulk: open each one and verify its figures.`}</p> : null}
      {rejecting && items.length > 0 ? (
        <RejectForm idPrefix="bulk-reject" label={t`Reason for rejecting`} button={t`Reject ${items.length}`} pending={reject.isPending}
          onReject={(body, done) => reject.mutate({ ...body, ids: items.map((a) => a.id) },
            { onSuccess: (r) => { done(); setRejecting(false); report(r, 'rejected'); }, onError: fail })} />
      ) : null}
    </div>
  );
}

function ArtifactDetail({ id }: { id: string }) {
  const { t } = useLingui();
  const { tenant, scopes } = useTenant();
  const { locale, tz } = useLocale();
  const when = (iso: string) => fmtDateTime(iso, { locale, tz }, { dateStyle: 'short', timeStyle: 'short' });
  const toast = useToast();
  const q = useAIArtifact(tenant.slug, id);
  const patch = usePatchArtifact(tenant.slug);
  const approve = useApproveArtifact(tenant.slug);
  const reject = useRejectArtifact(tenant.slug);
  const [edit, setEdit] = React.useState<{ headline: string; standfirst: string; summary: string } | null>(null);
  if (q.isPending) return <Skeleton className="h-96" />;
  if (q.isError) return <ErrorState error={q.error} onRetry={() => q.refetch()} labels={{ title: t`Could not load the item`, retry: t`Retry`, requestId: t`Request id`, copy: t`Copy` }} />;
  const a: AIArtifact = q.data;
  if (a.kind === 'numbers') return <NumbersCardDetail a={a} />; // v3 §3.4: figure-by-figure review, never auto-published
  const b = a.body as unknown as ArticleBody;
  const open = a.status === 'draft' || a.status === 'approved';
  const canWrite = can(scopes, AI_WRITE) && open;
  const canPublish = can(scopes, AI_PUBLISH) && open;
  const fail = (e: unknown) => toast.push({ title: t`The action failed`, description: problemText(e), tone: 'danger' });
  // an edit is a new version; the AI's original is kept and each changed field is recorded as a correction (v3 §1.5)
  const save = () => edit && patch.mutate({ id, body: { body: {
    headline: edit.headline.trim(), standfirst: edit.standfirst.trim(), ...(b.summary !== undefined ? { summary: edit.summary.trim() } : {}),
  } } },
    { onSuccess: () => { setEdit(null); toast.push({ title: t`Saved`, tone: 'ok' }); }, onError: fail });
  // a Hebrew artefact reads right to left as a whole (Dmitry 2026-10-09: "all the page in hebrew should be rtl"),
  // whatever the Studio language
  const he = /[\u0590-\u05FF]/.test(`${b.headline ?? ''}${b.standfirst ?? ''}${a.subject_title ?? ''}`);
  return (
    <article className="space-y-5" aria-labelledby="ai-h" dir={he ? 'rtl' : undefined} lang={he ? 'he' : undefined}>
      <div className="flex flex-wrap items-center gap-2">
        <AILabel a={a} />
        <Badge tone={a.status === 'published' ? 'ok' : a.status === 'rejected' ? 'danger' : 'neutral'}>
          {a.status === 'published' ? t`Published` : a.status === 'rejected' ? t`Rejected` : t`Draft`}
        </Badge>
        <span className="text-xs text-fg-muted">{a.subject_title}{a.channel ? ` · ${a.channel}` : ''}{a.subject_start_at ? ` · ${when(a.subject_start_at)}` : ''}</span>
      </div>
      {edit ? (
        <div className="space-y-3">
          <Field id="ai-headline" label={t`Headline`} hint={t`Up to 70 characters`}>
            <Input value={edit.headline} maxLength={120} onChange={(e) => setEdit({ ...edit, headline: e.target.value })} />
          </Field>
          <Field id="ai-standfirst" label={t`Standfirst`} hint={t`Up to 160 characters`}>
            <Input value={edit.standfirst} maxLength={300} onChange={(e) => setEdit({ ...edit, standfirst: e.target.value })} />
          </Field>
          {b.summary !== undefined ? (
            <div className="space-y-1">
              <label htmlFor="ai-summary" className="block text-sm font-medium">{t`Summary`}</label>
              <textarea id="ai-summary" dir="auto" rows={4} maxLength={2000} value={edit.summary} onChange={(e) => setEdit({ ...edit, summary: e.target.value })}
                className="w-full rounded-md border border-border bg-surface p-2 text-sm" />
            </div>
          ) : null}
          <p className="text-xs text-fg-muted">{t`Your edit is saved as a new version; the AI's original text is kept.`}</p>
          <div className="flex gap-2">
            <Button size="sm" onClick={save} disabled={patch.isPending || !edit.headline.trim()}>{t`Save`}</Button>
            <Button size="sm" variant="ghost" onClick={() => setEdit(null)}>{t`Cancel`}</Button>
          </div>
        </div>
      ) : (
        <header>
          <h2 id="ai-h" className="text-xl font-semibold">{b.headline}</h2>
          {b.standfirst ? <p className="mt-1 text-fg-muted">{b.standfirst}</p> : null}
          {canWrite ? <Button size="sm" variant="ghost" className="mt-1" onClick={() => setEdit({ headline: b.headline ?? '', standfirst: b.standfirst ?? '', summary: b.summary ?? '' })}>{t`Edit text`}</Button> : null}
        </header>
      )}
      {b.summary && !edit ? <section><h3 className="font-semibold">{t`Summary`}</h3><p className="mt-1 text-sm">{b.summary}</p></section> : null}
      {b.chapters?.length ? (
        <section>
          <h3 className="font-semibold">{t`Chapters`}</h3>
          <ol className="mt-2 space-y-3">
            {b.chapters.map((c, i) => (
              <li key={i} className="rounded-md border border-border p-3">
                <div className="flex items-baseline gap-2"><span className="font-mono text-xs text-fg-muted ltr-isolate">{clock(c.start_ms)}</span><span className="font-medium">{c.title}</span></div>
                {c.paragraphs.map((p, k) => <p key={k} className="mt-1 text-sm">{p}</p>)}
              </li>
            ))}
          </ol>
        </section>
      ) : null}
      {b.quotes?.length ? (
        <section>
          <h3 className="font-semibold">{t`Quotes`}</h3>
          <ul className="mt-2 space-y-2">
            {b.quotes.map((q2, i) => (
              <li key={i} className="border-s-4 border-primary ps-3 text-sm">
                <q>{q2.text}</q>{' '}
                <span className="text-xs text-fg-muted">— {q2.role === 'presenter' ? t`presenter` : q2.role === 'guest' ? t`guest` : q2.role === 'report' ? t`report` : t`speaker`} · <span className="ltr-isolate">{clock(q2.start_ms)}</span></span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {(b.entities?.length || b.tags?.length) ? (
        <section className="flex flex-wrap gap-1.5" aria-label={t`Names and tags`}>
          {b.entities?.map((e) => <Badge key={`e-${e}`} tone="primary">{e}</Badge>)}
          {b.tags?.map((x) => <Badge key={`t-${x}`} tone="neutral">#{x}</Badge>)}
        </section>
      ) : null}
      {a.status === 'rejected' ? <RejectedReason code={a.reject_code} note={a.reject_reason} /> : null}
      {canPublish || canWrite ? (
        <div className="space-y-3 border-t border-border pt-4">
          {canPublish ? (
            <Button disabled={approve.isPending} onClick={() => approve.mutate({ id, body: null },
              { onSuccess: () => toast.push({ title: t`Published`, description: t`Your site shows it and your webhooks received it.`, tone: 'ok' }), onError: fail })}>
              <Check className="size-4" aria-hidden />{t`Approve and publish`}
            </Button>
          ) : null}
          {canWrite ? (
            <RejectForm idPrefix="ai-reject" label={t`Reason for rejecting`} button={t`Reject`} pending={reject.isPending}
              onReject={(body, done) => reject.mutate({ id, body }, { onSuccess: () => { done(); toast.push({ title: t`Rejected`, tone: 'ok' }); }, onError: fail })} />
          ) : null}
        </div>
      ) : null}
      {a.status === 'published' && can(scopes, AI_WRITE) ? (
        <div className="border-t border-border pt-4">
          <RejectForm idPrefix="ai-unpublish" label={t`Take it off the site — reason`} button={t`Unpublish`} pending={reject.isPending}
            onReject={(body, done) => reject.mutate({ id, body }, { onSuccess: () => { done(); toast.push({ title: t`Taken off the site`, tone: 'ok' }); }, onError: fail })} />
        </div>
      ) : null}
      <EditHistory id={a.id} version={a.version ?? 1} />
      <p className="text-xs text-fg-muted">
        {t`Made`} {when(a.created_at)}{a.edited_by ? ` · ${t`edited by`} ${a.edited_by}` : ''}{a.reviewed_by ? ` · ${t`reviewed by`} ${a.reviewed_by}` : ''}
      </p>
    </article>
  );
}

/** 0111: the tenant's summaries in one review state — draft summaries are not on the site until approved. */
function SummaryList({ review, selected, onSelect }: { review: SummaryReview; selected?: string; onSelect: (id: string, kind: SummaryKind) => void }) {
  const { t } = useLingui();
  const { locale, tz } = useLocale();
  const q = useSummaryReviews(review);
  if (q.isPending) return <Skeleton className="h-48" />;
  if (q.isError) return <ErrorState error={q.error as never} onRetry={() => q.refetch()} labels={{ title: t`Could not load the summaries`, retry: t`Retry`, requestId: t`Request id`, copy: t`Copy` }} />;
  const items = q.data?.items ?? [];
  if (!items.length) return <EmptyState title={review === 'draft' ? t`No summaries waiting for review` : t`Nothing here`} />;
  return (
    <ul className="space-y-2" data-testid="summary-review-list">
      {items.map((x) => (
        <li key={x.subject_id}>
          <button type="button" onClick={() => onSelect(x.subject_id, x.subject_type)} aria-current={selected === x.subject_id ? 'true' : undefined}
            className={`w-full rounded-md border p-2 text-start text-sm hover:bg-surface-2 ${selected === x.subject_id ? 'border-primary' : 'border-border'}`}>
            <span className="block font-medium"><bdi>{x.title || t`(untitled)`}</bdi></span>
            <span className="block text-xs text-fg-muted">{x.start_at ? fmtDateTime(x.start_at, { locale, tz }) : t`Library video`}</span>
            <span className="mt-1 line-clamp-2 block text-xs" dir="rtl" lang="he">{x.summary}</span>
            <span className="mt-1 block"><SummaryReviewBadge review={x.review_status} /></span>
          </button>
        </li>
      ))}
    </ul>
  );
}
